import { api, formatApiErrorDetail } from "@/lib/api";
import { StatusPill } from "@/components/inst/ProductImage";
import { SpecPdfButton } from "@/components/inst/SpecPdfButton";
import { Pencil, Copy, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

export function MyModels({ models, onOpen, onChanged }) {
  const duplicate = async (m) => {
    try {
      const { data } = await api.post("/inst/my/models", { product_id: m.product_id, name: `${m.name} (copy)`, selections: m.selections });
      toast.success("Model duplicated"); onChanged(); onOpen(data.model);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.name}"?`)) return;
    try { await api.delete(`/inst/my/models/${m.id}`); toast.success("Model deleted"); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <section className="mt-12" data-testid="inst-my-models">
      <h2 className="font-display font-bold text-xl text-[#0F172A] mb-3">My Models</h2>
      {models.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-10 text-center text-sm text-zinc-400" data-testid="inst-my-models-empty">You haven't saved any models yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {models.map((m) => (
            <div key={m.id} className="bg-white border border-zinc-200 p-4 flex flex-col" data-testid={`inst-my-model-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-[#0F172A] truncate">{m.name}</div>
                  <div className="text-xs text-zinc-500">{m.product_name}{m.quantity ? ` · Qty ${m.quantity}` : ""}</div>
                  {m.model_number && <div className="mt-1 font-mono text-xs text-[#0F172A] break-all" data-testid={`inst-my-model-number-${m.id}`}>{m.model_number}</div>}
                </div>
                <StatusPill status={m.status} testid={`inst-my-model-status-${m.id}`} />
              </div>
              <div className="mt-3 text-xs text-zinc-500 space-y-0.5 flex-1">
                {Object.entries(m.selections || {}).slice(0, 4).map(([k, v]) => <div key={k} className="truncate"><span className="text-zinc-400">{k}:</span> {v}</div>)}
                {Object.keys(m.selections || {}).length > 4 && <div className="text-zinc-400">+{Object.keys(m.selections).length - 4} more</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-4 text-xs text-zinc-500">
                <span className="mr-auto font-mono text-zinc-400">{(m.updated_at || "").slice(0, 10)}</span>
                {m.status === "Draft" ? (
                  <button onClick={() => onOpen(m)} data-testid={`inst-my-model-edit-${m.id}`} className="flex items-center gap-1 hover:text-[#0F172A]"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                ) : (
                  <button onClick={() => onOpen(m)} data-testid={`inst-my-model-view-${m.id}`} className="flex items-center gap-1 hover:text-[#0F172A]"><Eye className="h-3.5 w-3.5" /> View</button>
                )}
                <SpecPdfButton model={m} testid={`inst-my-model-pdf-${m.id}`} />
                <button onClick={() => duplicate(m)} data-testid={`inst-my-model-duplicate-${m.id}`} className="flex items-center gap-1 hover:text-[#0F172A]"><Copy className="h-3.5 w-3.5" /> Duplicate</button>
                {m.status === "Draft" && <button onClick={() => remove(m)} data-testid={`inst-my-model-delete-${m.id}`} className="flex items-center gap-1 hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
